import { lineIdOf } from "../utils/variantCore";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import LocationPrompt from "./LocationPrompt";
import {
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { ReactComponent as EmptyCartSVG } from "../assets/img/Emptycart.svg";
import {
  Add,
  Remove,
  Close,
  InfoOutlined,
  StorefrontOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import {
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
  clearCart,
} from "../redux/brandCartSlice";
import { formatCurrency } from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { StruckPrice, VendorOfferLine } from "./PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import {
  linesSubtotal,
  effectiveMinimum,
  minimumOrderShortfall,
  minimumOrderMessage,
} from "../utils/minimumOrder";
import MinimumOrderNotice from "./MinimumOrderNotice";
/* A quote-only line CAN sit in this cart. Two ways in: the brand product page
   lost the posture on a cold load until the server started mapping every public
   product read (so "USh 0" sat beside a live Add to cart), and this cart is
   persisted to localStorage under "brandCart", so a line added then is still
   here after the server fix. Either way it has to be handled, not assumed away. */
import {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  cartLineProblem,
  partitionCartLines,
  unsellableLinesMessage,
} from "../utils/priceOnRequest";
import useCoverage from '../redux/useCoverage';
import CoverageBanner from './CoverageBanner';
import { cartOutletId, findOutlet } from "../utils/vendorOutlets";
import { colorWithAlpha, getBrandTheme } from "../utils/brandTheme";
import BranchCartCheck from "./branch/BranchCartCheck";
import "./RestaurantCart.css";

const BrandCart = ({ onClose, brandId, brand }) => {
  const dispatch = useDispatch();
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const history = useHistory();
  const [showClearDialog, setShowClearDialog] = useState(false);
  const currentBrand = useSelector((state) => state.brandCart.currentBrand);
  // Use brandId prop if provided, otherwise use currentBrand from state
  const activeBrandId = brandId || currentBrand;

  const allCarts = useSelector((state) => state.brandCart.carts);
  const cart = allCarts[activeBrandId] || {};
  // The outlet (branch) this cart is fulfilled from, read off its lines the
  // way the restaurant cart reads it; null for the main store.
  const outletId = cartOutletId(cart);
  // Pre-checkout coverage gate (backend /api/coverage — same decision as the
  // order-create gate) so the customer can't proceed to an undeliverable order.
  // An outlet is checked against ITS coordinates and radius, not the brand's.
  const coverage = useCoverage({ vendorId: activeBrandId, outletId, vendorType: 'brand' });
  const selectedBrand = useSelector((state) => state.brands.selectedBrand);
  const displayBrand = brand || selectedBrand;
  const fulfilmentOutlet = findOutlet(displayBrand, outletId);
  const theme = getBrandTheme(displayBrand || {});
  const brandInk = theme.onPrimary === "#0f172a" ? "#0f172a" : theme.primary;

  const cartItems = Object.values(cart).filter(
    (item) => (Number(item.quantity) || 0) > 0
  );

  /* Minimum order value for THIS brand, goods only.
     `fulfilment.minimumOrderValueUgx` is the brand's own floor and has been on
     screen since the product page shipped ("Min. order …") without anything
     ever checking it, so a customer could read it, ignore it and reach
     checkout. A brand floor can only RAISE the platform one, never lower it.
     The brand document reaches this modal either as the `brand` prop or as
     state.brands.selectedBrand; on the rare render where neither is loaded yet
     the platform floor applies on its own and the server still refuses the
     rest. */
  const brandMinimum = Number(displayBrand?.fulfilment?.minimumOrderValueUgx) || 0;
  const minimumOrderValue = useMinimumOrderValue();
  const orderMinimum = effectiveMinimum(minimumOrderValue, brandMinimum);

  /* WHAT MAY ACTUALLY BE ORDERED, and what may not.
     The floor is measured against the PRICED lines alone. Measuring it against
     every line was wrong in both directions. A cart holding nothing but
     price-nulled lines subtotalled to 0, and `minimumOrderShortfall` returns 0
     for a subtotal of 0 on purpose (so an empty cart is never nagged), so such a
     cart CLEARED the floor, lit up Proceed to Checkout and sent the shopper to a
     checkout the server then refuses. And a quote-only line that still carried a
     price from before the vendor flipped the posture counted towards a floor it
     will never be billed against. Excluding those lines also means the
     minimum-order message can no longer appear for a cart whose items have no
     prices: there is no shortfall to quote, only a line to remove. */
  const linePosture = partitionCartLines(cartItems);
  const goodsSubtotal = linesSubtotal(linePosture.sellable);
  const minimumShortfall = minimumOrderShortfall(goodsSubtotal, orderMinimum);
  const unsellableNotice = unsellableLinesMessage(linePosture);

  const canProceed =
    cartItems.length > 0 &&
    /* The server refuses the WHOLE order over one unchargeable line, so there
       is no partial checkout to offer: the line comes out first. */
    linePosture.unsellable.length === 0 &&
    minimumShortfall === 0 &&
    coverage.hasLocation &&
    coverage.isConfirmed;

  const handleIncrease = (product) => {
    if (!activeBrandId) {
      console.error("No active brand ID for increment");
      return;
    }
    dispatch(
      incrementQuantity({ brandId: activeBrandId, productId: lineIdOf(product) })
    );
  };

  const handleDecrease = (product) => {
    if (!activeBrandId) return;
    const minQty = product.minOrderQty || 1;
    const currentQty = Number(product.quantity) || 0;
    if (currentQty <= minQty) {
      toast.warning(
        `Minimum order is ${minQty} unit${minQty !== 1 ? "s" : ""}. Remove the item instead.`,
        { toastId: `min-qty-${product._id}` }
      );
      return;
    }
    dispatch(
      decrementQuantity({ brandId: activeBrandId, productId: lineIdOf(product) })
    );
  };

  const handleRemove = (productId) => {
    if (!activeBrandId) {
      console.error("No active brand ID for remove");
      return;
    }
    dispatch(removeFromCart({ brandId: activeBrandId, productId }));
  };

  const checkoutHandler = () => {
    if (!canProceed) return;
    history.push("/brand-shipping");
    if (onClose) onClose();
  };

  const handleClearCart = () => {
    if (!activeBrandId) {
      console.error("No active brand ID for clear cart");
      return;
    }
    dispatch(clearCart(activeBrandId));
    setShowClearDialog(false);
  };

  const handleOpenClearDialog = () => {
    setShowClearDialog(true);
  };

  const handleCloseClearDialog = () => {
    setShowClearDialog(false);
  };

  return (
    <div
      className="restaurant-cart-container brand-cart-themed"
      style={{
        "--rc-brand": theme.primary,
        "--rc-brand-ink": brandInk,
        "--cart-primary": theme.secondary,
        "--cart-primary-hover": theme.secondaryHover,
        "--cart-on-primary": theme.onSecondary,
        "--cart-shadow": colorWithAlpha(theme.secondary, 0.3),
        "--cart-secondary": theme.secondary,
        "--cart-secondary-soft": theme.secondarySoft,
      }}
    >
      {/* Cart Header */}
      <div className="restaurant-cart-header">
        <h2 className="restaurant-cart-title">Your Cart</h2>
        {displayBrand && (
          <p className="restaurant-cart-subtitle">
            <StorefrontOutlined className="restaurant-cart-subtitle-icon" />
            {displayBrand.tradingName || displayBrand.name}
            {fulfilmentOutlet ? ` (${fulfilmentOutlet.name})` : ""}
          </p>
        )}
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {cartItems.length > 0 && (
            <IconButton
              onClick={handleOpenClearDialog}
              aria-label="clear cart"
              style={{ color: "#d32f2f" }}
              title="Clear Cart"
            >
              <DeleteOutline />
            </IconButton>
          )}
          <IconButton
            className="restaurant-cart-close-btn"
            onClick={onClose}
            aria-label="close cart"
          >
            <Close />
          </IconButton>
        </div>
      </div>

      {/* Cart Content */}
      <div className="restaurant-cart-content">
        {cartItems.length === 0 ? (
          <div className="restaurant-cart-empty">
            <div className="restaurant-cart-empty-icon-wrapper">
              <EmptyCartSVG className="restaurant-cart-empty-icon" />
            </div>
            <h3 className="restaurant-cart-empty-title">Your Cart is Empty</h3>
            <p className="restaurant-cart-empty-text">
              You haven't added any products yet. Start exploring and add them
              to your cart!
            </p>
          </div>
        ) : (
          <ul className="restaurant-cart-items-list">
            {cartItems.map((item) => {
              const optionsTotal = (item.options || []).reduce(
                (sum, option) => sum + (Number(option.price) || 0),
                0
              );
              // Include the buyer's per-unit variant adjustment so the
              // line total matches the running preview from the detail
              // modal and the order summary on checkout.
              const specDelta = Number(item.specPriceDelta) || 0;
              /* "QUOTE" (a posture, with a price behind a conversation) and
                 "UNPRICED" (nobody has costed this yet) need different words and
                 neither has a line total worth printing. */
              const problem = cartLineProblem(item);
              const itemTotal =
                ((Number(item.price) || 0) + optionsTotal + specDelta) *
                (Number(item.quantity) || 0);
              const listTotal = lineHasDeal(item)
                ? ((Number(item.listPrice) || 0) + optionsTotal + specDelta) *
                  (Number(item.quantity) || 0)
                : null;
              const specEntries = item.selectedSpecs
                ? Object.entries(item.selectedSpecs).filter(
                  ([, v]) => v !== undefined && v !== null && v !== ""
                )
                : [];

              return (
                <li key={lineIdOf(item)} className="restaurant-cart-item">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="restaurant-cart-item-image"
                    onError={(e) => {
                      e.target.src = "/logo512.png";
                    }}
                  />

                  <div className="restaurant-cart-item-details">
                    <h4 className="restaurant-cart-item-name">{item.name}</h4>
                    {specEntries.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                          margin: "2px 0 6px",
                        }}
                      >
                        {specEntries.map(([k, v]) => (
                          <span
                            key={k}
                            style={{
                              fontSize: "0.7rem",
                              background: theme.secondarySoft,
                              color: brandInk,
                              border: `1px solid ${colorWithAlpha(theme.secondary, 0.42)}`,
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontWeight: 600,
                            }}
                          >
                            {k}: {String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="restaurant-cart-item-price">
                      {problem ? (
                        problem === "QUOTE"
                          ? PRICE_ON_REQUEST_LABEL
                          : PRICE_UNAVAILABLE_LABEL
                      ) : (
                        <>
                          {listTotal !== null && (
                            <StruckPrice>
                              {formatCurrency(listTotal)}
                            </StruckPrice>
                          )}
                          {formatCurrency(itemTotal)}
                        </>
                      )}
                    </p>
                    {problem && (
                      <p
                        style={{
                          margin: "2px 0 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.35,
                          color: "#b45309",
                          fontWeight: 600,
                        }}
                      >
                        {/* Terse on the line, because the footer notice below
                            carries the instruction. Naming the state here is
                            what lets a shopper with a mixed cart see WHICH line
                            the notice is about. */}
                        {problem === "QUOTE"
                          ? "Needs a vendor quote"
                          : "No price published yet"}
                      </p>
                    )}

                    <div className="restaurant-cart-item-actions">
                      {/* No stepper on a line that cannot be ordered: every
                          press would edit a quantity nobody can be charged for.
                          Remove is the only move, and it is already here. */}
                      {!problem && (
                        <div className="restaurant-cart-quantity-controls">
                          <IconButton
                            className="restaurant-cart-quantity-btn decrease"
                            onClick={() => handleDecrease(item)}
                            size="small"
                            aria-label="decrease quantity"
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <span className="restaurant-cart-quantity-display">
                            {Number(item.quantity) || 0}
                          </span>
                          <IconButton
                            className="restaurant-cart-quantity-btn increase"
                            onClick={() => handleIncrease(item)}
                            size="small"
                            aria-label="increase quantity"
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </div>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemove(lineIdOf(item))}
                        style={{ marginLeft: "10px", fontSize: "0.75rem" }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Cart Footer */}
      <div className="restaurant-cart-footer">
        <CoverageBanner
          variant="inline"
          deliverable={coverage.deliverable}
          reason={coverage.reason}
          distanceKm={coverage.distanceKm}
          radius={coverage.radius}
          coverageCountry={coverage.coverageCountry}
          hasLocation={coverage.hasLocation}
          status={coverage.status}
          error={coverage.error}
          onRetry={coverage.refresh}
          onChangeLocation={() => setShowLocationPrompt(true)}
        />
        {unsellableNotice && (
          <div
            style={{
              margin: "0 0 10px",
              padding: "10px 12px",
              borderRadius: 8,
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              color: "#92400e",
              fontSize: "0.8rem",
              lineHeight: 1.4,
              fontWeight: 600,
            }}
          >
            {unsellableNotice}
          </div>
        )}
        <MinimumOrderNotice
          shortfall={minimumShortfall}
          minimum={orderMinimum}
        />
        {/* Savings on the ORDERABLE lines only: a line the order cannot be
            charged for cannot save anybody anything. */}
        <VendorOfferLine
          amount={cartOfferSavings(linePosture.sellable)}
          format={formatCurrency}
        />
        <div className="restaurant-cart-total">
          <span className="restaurant-cart-total-label">Total</span>
          <span className="restaurant-cart-total-value">
            {/* The ORDERABLE goods, not the slice's `total`. That number is
                recomputed for `state.brandCart.currentBrand`, which is not
                necessarily the brand this modal was opened for (the `brandId`
                prop wins here), and it counts a quote-only line that still
                carries an old price. `linesSubtotal` is the same money rule the
                floor above uses, so the two can never disagree. */}
            {formatCurrency(goodsSubtotal)}
          </span>
        </div>

        <Button
          className="restaurant-cart-checkout-btn"
          onClick={checkoutHandler}
          disabled={!canProceed}
          variant="contained"
        >
          {/* The unorderable lines come first: a shortfall quoted against a
              subtotal that excludes them would read as the wrong problem. */}
          {linePosture.unsellable.length > 0
            ? linePosture.allUnsellable
              ? "Quote needed before ordering"
              : "Remove the items that need a quote"
            : minimumShortfall > 0
            ? minimumOrderMessage(minimumShortfall, formatCurrency)
            : coverage.isFailed
              ? "Delivery check failed, retry above"
              : coverage.isOutOfRange
                ? "Vendor doesn't deliver here"
                : !coverage.hasLocation
                  ? "Set your delivery location"
                  : !coverage.isConfirmed
                    ? "Checking delivery coverage…"
                    : "Proceed to Checkout"}
        </Button>

        <div className="restaurant-cart-savings">
          <Typography className="restaurant-cart-savings-text">
            <InfoOutlined className="restaurant-cart-savings-icon" />
            Review your items before checkout
          </Typography>
        </div>
      </div>

      {/* Clear Cart Confirmation Dialog */}
      <Dialog
        open={showClearDialog}
        onClose={handleCloseClearDialog}
        aria-labelledby="clear-cart-dialog-title"
        aria-describedby="clear-cart-dialog-description"
      >
        <DialogTitle id="clear-cart-dialog-title">Clear Cart?</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-cart-dialog-description">
            Are you sure you want to remove all items from your cart? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleClearCart}
            color="error"
            variant="contained"
            autoFocus
          >
            Clear Cart
          </Button>
        </DialogActions>
      </Dialog>
      {showLocationPrompt && (
        <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
      )}
      {/* Opening the cart checks it at its branch (4 Oct 2026): a cart read
          back from localStorage, moved, or touched by a live branch change
          shows "Changes at <branch>" before checkout, not after. */}
      <BranchCartCheck vertical="brand" cartId={activeBrandId} />
    </div>
  );
};

export default BrandCart;
